import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Headphones, X } from "lucide-react";
import { useLang } from "../i18n";

const LIVE_SUPPORT_URL = "https://giris.parasyo.com/destek";
const SUPPORT_EVENT = "parasyo:open-support-chat";
const HINT_KEY = "parasyo-support-hint-shown";

export const openSupportChat = () => {
  window.dispatchEvent(new CustomEvent(SUPPORT_EVENT));
};

export const SupportChat = () => {
  const { t } = useLang();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [hintVisible, setHintVisible] = useState(false);
  const isAdmin = pathname.startsWith("/admin");
  const supportUrl = process.env.NODE_ENV === "production" ? LIVE_SUPPORT_URL : `${window.location.origin}/destek`;

  const openPanel = useCallback(() => {
    sessionStorage.setItem(HINT_KEY, "1");
    setHintVisible(false);
    setHasOpened(true);
    setOpen(true);
  }, []);

  useEffect(() => {
    window.addEventListener(SUPPORT_EVENT, openPanel);
    return () => window.removeEventListener(SUPPORT_EVENT, openPanel);
  }, [openPanel]);

  useEffect(() => {
    if (open || sessionStorage.getItem(HINT_KEY)) return undefined;
    const showTimer = setTimeout(() => setHintVisible(true), 5000);
    return () => clearTimeout(showTimer);
  }, [open]);

  useEffect(() => {
    if (!hintVisible) return undefined;
    sessionStorage.setItem(HINT_KEY, "1");
    const hideTimer = setTimeout(() => setHintVisible(false), 5200);
    return () => clearTimeout(hideTimer);
  }, [hintVisible]);

  useEffect(() => {
    const mobile = window.matchMedia("(max-width: 767px)");
    const applyScrollLock = () => {
      const locked = open && mobile.matches && !isAdmin;
      document.body.style.overflow = locked ? "hidden" : "";
      if (locked) window.__parasyoLenis?.stop();
      else window.__parasyoLenis?.start();
    };
    applyScrollLock();
    mobile.addEventListener("change", applyScrollLock);
    return () => {
      mobile.removeEventListener("change", applyScrollLock);
      document.body.style.overflow = "";
      window.__parasyoLenis?.start();
    };
  }, [open, isAdmin]);

  if (isAdmin) return null;

  return (
    <>
      {hintVisible && !open && (
        <div
          data-testid="support-chat-hint"
          className="fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] right-5 z-[80] rounded-2xl rounded-br-sm border border-white/10 bg-operofy-darksurface px-4 py-3 text-sm font-semibold text-white shadow-2xl md:bottom-24 md:right-6"
          role="status"
        >
          {t.support.hint}
          <span className="absolute -bottom-1 right-7 h-3 w-3 rotate-45 border-b border-r border-white/10 bg-operofy-darksurface" />
        </div>
      )}

      <button
        type="button"
        data-testid="support-chat-button"
        onClick={openPanel}
        aria-expanded={open}
        aria-label={t.support.button}
        className="fixed bottom-[calc(2rem+env(safe-area-inset-bottom))] right-5 z-[80] flex h-14 w-14 items-center justify-center rounded-full bg-operofy-blue px-0 text-sm font-bold text-white shadow-[0_16px_45px_rgba(13,141,66,0.35)] transition-transform duration-300 hover:scale-105 active:scale-95 md:bottom-6 md:right-6 md:w-auto md:gap-2.5 md:px-5"
      >
        <Headphones size={20} strokeWidth={2} />
        <span className="hidden md:inline">{t.support.button}</span>
      </button>

      {hasOpened && (
        <div
          data-testid="support-chat-panel"
          role="dialog"
          aria-modal="true"
          aria-label={t.support.panelTitle}
          aria-hidden={!open}
          inert={!open}
          className={`fixed inset-0 z-[90] flex flex-col overflow-hidden bg-operofy-dark shadow-2xl transition-all duration-300 md:inset-auto md:bottom-6 md:right-6 md:h-[620px] md:w-[400px] md:rounded-[1.75rem] md:border md:border-white/10 ${
            open ? "visible translate-y-0 opacity-100 md:scale-100" : "invisible translate-y-6 opacity-0 md:scale-[0.98]"
          }`}
        >
          <div className="flex h-16 shrink-0 items-center justify-between border-b border-white/10 bg-operofy-darksurface px-5 md:h-14">
            <div className="min-w-0">
              <h2 className="truncate font-display text-base font-bold text-white" data-testid="support-chat-title">
                {t.support.panelTitle}
              </h2>
              <p className="truncate text-[11px] text-white/45">{t.support.panelSub}</p>
            </div>
            <button
              type="button"
              data-testid="support-chat-close"
              onClick={() => setOpen(false)}
              aria-label={t.support.close}
              className="ml-4 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/10 text-white/70 transition-colors duration-200 hover:border-white/30 hover:text-white"
            >
              <X size={18} strokeWidth={2} />
            </button>
          </div>

          <div className="min-h-0 flex-1 bg-white">
            <iframe
              src={supportUrl}
              title={t.support.panelTitle}
              data-testid="support-chat-iframe"
              style={{ width: "100%", height: "100%", border: 0 }}
              allow="clipboard-write"
            />
          </div>

          <div className="flex h-11 shrink-0 items-center justify-center border-t border-white/10 bg-operofy-darksurface px-4">
            <a
              href="/sayfa/gizlilik-politikasi"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="support-privacy-link"
              className="text-xs font-semibold text-white/50 transition-colors duration-200 hover:text-operofy-blue"
            >
              {t.support.privacy}
            </a>
          </div>
        </div>
      )}
    </>
  );
};
